// Die Sensoren aus dem Homelab-Protokoll (Topics `sensor.stand` und
// `klima.bewertung`; docs/protokoll.md › „Sensoren über das Protokoll“) in
// genau die Formen, die die Seiten aus den REST-Antworten kennen:
//   - GET /v2-api/climate    → `ClimateDevice`  (quelle = klima, nur Geräte
//     MIT Temperatur -- REST filtert die übrigen heraus),
//   - GET /v2-api/co2monitor → `Co2MonitorDevice` (quelle = co2),
//   - GET /v2-api/lipoguard  → `LipoguardDevice`  (quelle = lipo),
//   - GET /v2-api/waterleak  → `WaterleakDevice`  (quelle = wasser),
//   - GET /v2-api/klima/summary → `KlimaSensor` je Sensor der Übersicht; die
//     Lage dazu (status, ok_sensors, total_sensors) zählt `klimaLage`.
// Ein Stand je Gerät (`sensor.<id>.stand`) ergibt die Form seiner Quelle.
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/sensoren-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert
// an (`skaliere("sensor.stand", …)`: °C, %, hPa); Gegenstück der Rechnung ist
// der Adapter in internal/protokoll/adapter.
//
// Regel, mit der die REST-Antworten exakt wiederkehren (der Adapter sendet
// spiegelbildlich):
//   - Felder, die die REST-Antwort ohne `omitempty` führt (Zeiger der Geräte
//     ohne Messwert, Zeiten), stehen immer: fehlt der Wert im Stand, ist er
//     `null` (Zahlen, Wahrheitswerte der Zeiger) bzw. die Nullzeit, die Go für
//     `time.Time{}` schreibt ("0001-01-01T00:00:00Z"). Texte und Wahrheits-
//     werte ohne Zeiger (status, water_leak) fallen auf ""/false zurück.
//   - `KlimaSensor` führt battery, reason, disabled, since, aussen und
//     warn_after_s mit `omitempty`: sie stehen am Ergebnis nur, wenn der Stand
//     sie trägt (battery auch als 0, disabled/aussen nur als true, reason/
//     since nur nicht leer, warn_after_s nur ungleich 0).
//   - Die Id steht nur im Instanznamen („sensor.<16 Hex>.stand“, „klima.<16
//     Hex>.bewertung“, ein Hash); Name und Quelle stehen im Stand.

import type { SensorStand, KlimaBewertung, SensorWert } from "./protokoll/schema.gen";
import type {
  ClimateDevice,
  Co2MonitorDevice,
  KlimaLage,
  KlimaSensor,
  LipoguardDevice,
  LipoguardSensor,
  WaterleakDevice,
} from "./types";

/** So schreibt Go `time.Time{}` als JSON. */
export const NULLZEIT = "0001-01-01T00:00:00Z";

const HEX = /^[0-9a-f]+$/;

/** Instanzname → Id des Sensors: „sensor.ab12cd34ef56ab78.stand“ → „ab12cd34ef56ab78“. Alles andere → undefined. */
export function sensorIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "sensor" || t[2] !== "stand" || !HEX.test(t[1])) return undefined;
  return t[1];
}

/** Instanzname → Id der Bewertung: „klima.ab12cd34ef56ab78.bewertung“ → „ab12cd34ef56ab78“. Alles andere → undefined. */
export function bewertungIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "klima" || t[2] !== "bewertung" || !HEX.test(t[1])) return undefined;
  return t[1];
}

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const z = <T>(v: T | null | undefined): T | null => v ?? null;

/** Unix-Millisekunden → ISO-Text; fehlt/unlesbar → die Nullzeit der REST-Antwort. */
function zeit(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return NULLZEIT;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? NULLZEIT : d.toISOString();
}

// ── Geräte ─────────────────────────────────────────────────────────────

/** Das Klima-Gerät aus seinem Stand, wie `GET /v2-api/climate` es führt. Geräte
 *  ohne Temperatur führt REST nicht → undefined (ebenso jede andere Quelle). */
export function klimaGeraetAusProtokoll(st: SensorStand): ClimateDevice | undefined {
  if (st.quelle !== "klima" || st.temperatur == null) return undefined;
  return {
    name: s(st.name),
    temperature: st.temperatur,
    humidity: z(st.feuchte),
    pressure: z(st.druck),
    battery: z(st.batterie),
    link_quality: z(st.funk),
    occupancy: z(st.bewegung),
    contact: z(st.kontakt),
    contact_since: zeit(st.kontakt_seit),
    last_seen: zeit(st.zuletzt),
  };
}

function messwerte(werte: readonly SensorWert[] | null | undefined): Record<string, LipoguardSensor> {
  const o: Record<string, LipoguardSensor> = {};
  for (const w of werte ?? []) {
    if (w.name == null) continue;
    o[w.name] = { value: w.wert ?? 0, ts: zeit(w.zeit) };
  }
  return o;
}

/** Der CO2-Monitor aus seinem Stand, wie `GET /v2-api/co2monitor` ihn führt
 *  (`id` = Name im Stand). Andere Quellen → undefined. */
export function co2GeraetAusProtokoll(st: SensorStand): Co2MonitorDevice | undefined {
  if (st.quelle !== "co2") return undefined;
  return { id: s(st.name), status: s(st.status), status_ts: zeit(st.status_zeit), sensors: messwerte(st.werte), last_seen: zeit(st.zuletzt) };
}

/** Der LiPo-Wächter aus seinem Stand, wie `GET /v2-api/lipoguard` ihn führt.
 *  Andere Quellen → undefined. */
export function lipoGeraetAusProtokoll(st: SensorStand): LipoguardDevice | undefined {
  if (st.quelle !== "lipo") return undefined;
  return { id: s(st.name), status: s(st.status), status_ts: zeit(st.status_zeit), sensors: messwerte(st.werte), last_seen: zeit(st.zuletzt) };
}

/** Der Wassermelder aus seinem Stand, wie `GET /v2-api/waterleak` ihn führt.
 *  Andere Quellen → undefined. */
export function wasserGeraetAusProtokoll(st: SensorStand): WaterleakDevice | undefined {
  if (st.quelle !== "wasser") return undefined;
  return {
    name: s(st.name),
    water_leak: st.wasser ?? false,
    battery: z(st.batterie),
    link_quality: z(st.funk),
    last_seen: zeit(st.zuletzt),
  };
}

// Go vergleicht Texte Byte für Byte, hier Codeeinheit für Codeeinheit -- gleich,
// solange kein Name Zeichen außerhalb der Grundebene trägt.
function vergleicheText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Die Reihenfolge von `GET /v2-api/climate` und `/waterleak` (nach name). */
export function vergleicheNachName(a: { name: string }, b: { name: string }): number {
  return vergleicheText(a.name, b.name);
}

/** Die Reihenfolge von `GET /v2-api/co2monitor` und `/lipoguard` (nach id). */
export function vergleicheNachId(a: { id: string }, b: { id: string }): number {
  return vergleicheText(a.id, b.id);
}

// ── Klima-Übersicht ────────────────────────────────────────────────────

/** Der Sensor der Klima-Übersicht aus seiner Bewertung, wie
 *  `GET /v2-api/klima/summary` ihn in `sensors` führt. */
export function klimaSensorAusProtokoll(b: KlimaBewertung): KlimaSensor {
  const metrics: Record<string, number> = {};
  for (const w of b.werte ?? []) {
    if (w.name != null && w.wert != null) metrics[w.name] = w.wert;
  }
  const o: KlimaSensor = {
    domain: s(b.domain) as KlimaSensor["domain"],
    sensor_id: s(b.sensor),
    ts: s(b.zeit),
    metrics,
    status: s(b.status) as KlimaSensor["status"],
  };
  if (b.batterie != null) o.battery = b.batterie;
  if (b.grund) o.reason = b.grund;
  if (b.aus) o.disabled = true;
  if (b.seit) o.since = b.seit;
  if (b.aussen) o.aussen = true;
  if (b.warn_nach) o.warn_after_s = b.warn_nach;
  return o;
}

/** Schlüssel eines Sensors der Übersicht: Domain und Sensor-Id (dieselbe Id
 *  kann in zwei Domains vorkommen). */
export function klimaSchluessel(domain: string, sensorId: string): string {
  return domain + "\u0000" + sensorId;
}

const DOMAIN_REIHE: Readonly<Record<string, number>> = { climate: 0, motion: 1, door: 2, co2: 3, lipo: 4, waterleak: 5 };

// Unbekannte Domains hinter die bekannten.
const reihe = (d: string): number => DOMAIN_REIHE[d] ?? Object.keys(DOMAIN_REIHE).length;

/** Die Reihenfolge von `sensors` in `GET /v2-api/klima/summary`: nach Domain
 *  (climate, motion, door, co2, lipo, waterleak), dann nach sensor_id. */
export function vergleicheKlimaSensoren(a: Pick<KlimaSensor, "domain" | "sensor_id">, b: Pick<KlimaSensor, "domain" | "sensor_id">): number {
  const d = reihe(a.domain) - reihe(b.domain);
  if (d !== 0) return d;
  if (a.domain !== b.domain) return vergleicheText(a.domain, b.domain);
  return vergleicheText(a.sensor_id, b.sensor_id);
}

/** Die Lage der Übersicht wie im Backend (computeKlimaStatus plus Zählung):
 *  Deaktivierte zählen nicht; `ok_sensors` sind die aktiven mit Status "ok";
 *  problem vor warning vor ok. */
export function klimaLage(sensors: readonly Pick<KlimaSensor, "status" | "disabled">[]): KlimaLage {
  let total = 0;
  let ok = 0;
  let warnung = false;
  let problem = false;
  for (const x of sensors) {
    if (x.disabled) continue;
    total++;
    if (x.status === "ok") ok++;
    else if (x.status === "problem") problem = true;
    else if (x.status === "warning") warnung = true;
  }
  return { status: problem ? "problem" : warnung ? "warning" : "ok", ok_sensors: ok, total_sensors: total };
}
